'use client'

import React from 'react'
import { cn } from '@/lib/utils'
import LcIcon from './icons'

export type InputProps = Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size'> & {
  size?: 'sm' | 'md'
  leading?: React.ReactNode
  trailing?: React.ReactNode
  invalid?: boolean
  classInput?: string
}

/**
 * Figma text field: white, radius 8, `0 0 0 1px #E9E9E9` ring + soft 4px drop.
 * `sm` is the 32px in-card search; `md` is the 40px page-level field.
 */
const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, classInput, size = 'sm', leading, trailing, invalid, disabled, ...props }, ref) => (
    <label
      className={cn(
        'group/input relative flex items-center gap-2 overflow-hidden bg-surface text-ink shadow-input',
        'transition-[box-shadow,background-color] duration-[160ms] ease-out-strong',
        'focus-within:shadow-[0_4px_4px_-3px_rgba(0,0,0,0.1),0_0_0_1px_rgb(var(--lc-brand)),0_0_0_4px_rgb(var(--lc-brand)/0.14)]',
        invalid && 'shadow-[0_0_0_1px_#EF4444,0_0_0_4px_rgba(239,68,68,0.12)]',
        disabled && 'cursor-not-allowed opacity-60',
        size === 'sm' ? 'h-8 rounded-[8px] px-2' : 'h-10 rounded-[10px] px-3',
        className
      )}
    >
      {leading && (
        <span className="inline-flex shrink-0 text-ink-4 transition-colors group-focus-within/input:text-ink-2 [&>svg]:size-4">
          {leading}
        </span>
      )}
      <input
        ref={ref}
        disabled={disabled}
        className={cn(
          'min-w-0 flex-1 bg-transparent text-[14px] leading-[1.4] tracking-[-0.2px] text-ink outline-none placeholder:text-ink-4',
          'disabled:cursor-not-allowed',
          classInput
        )}
        {...props}
      />
      {trailing && <span className="inline-flex shrink-0 text-ink-4 [&>svg]:size-4">{trailing}</span>}
    </label>
  )
)
Input.displayName = 'Input'

export const SearchInput = React.forwardRef<HTMLInputElement, Omit<InputProps, 'leading'>>(
  ({ placeholder = 'Search', ...props }, ref) => (
    <Input
      ref={ref}
      type="search"
      inputMode="search"
      autoComplete="off"
      placeholder={placeholder}
      leading={<LcIcon name="search" />}
      classInput="[&::-webkit-search-cancel-button]:hidden"
      {...props}
    />
  )
)
SearchInput.displayName = 'SearchInput'

export default Input
